import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  QAlertBox,
  QAlertDescription,
  QAlertTitle,
  QBadge,
  QBox,
  QButton,
  QButtonGroup,
  QDivider,
  QFlex,
  QHeader,
  QHorizontalMetadata,
  QLastModifiedMetadata,
  QOpenPropertiesPanelButton,
  QOwnerMetadata,
  QSpacer,
  QTag,
  QText,
  QTitle,
  QVersionMetadata,
} from '@qualio/ui-components';
import { TEMPLATE_STATUS, TemplateDetail, TemplateVersions } from '../../types';
import { getOwnerText, getTagColor, getTagText } from '../../utils/textUtil';
import { TemplateHeaderButtons } from './TemplateHeaderButtons';
import { useNavigate } from 'react-router-dom';
import { OldDraftsAlert } from '../UpdateDrafts/Alert/OldDraftsAlert';
import { TemplateVersionSelect } from '../TemplateVersionSelect/TemplateVersionSelect';
import { ExportDocumentTemplateButton } from './Export/ExportTemplateButton';
import { InheritedTemplateIcon } from '../shared/InheritedTemplateIcon/InheritedTemplateIcon';
import { useTemplateActions } from '../../hooks/useTemplateActions';

type TemplateDetailsHeaderProps = {
  data: TemplateDetail;
  versions: TemplateVersions;
  onSaveSections?: () => void;
  onBeforeMakeEffective?: () => Promise<void>;
};

export const TemplateDetailsHeader: React.FC<TemplateDetailsHeaderProps> = ({
  data,
  versions,
  onSaveSections,
  onBeforeMakeEffective,
}) => {
  const { t } = useTranslation();
  const { isInherited, canExport, canUpgradeDocuments } = useTemplateActions(data);
  const isEffective = data.status === TEMPLATE_STATUS.effective;
  const hasNewerDraft = !!data.latest_template_id && data.id !== data.latest_template_id;
  const showDraftInProgressAlert = !isInherited && isEffective && hasNewerDraft;
  const navigate = useNavigate();
  const showDraft = useCallback(() => {
    navigate(`/doc-templates/${data?.latest_template_id}`, { replace: true });
  }, [data, navigate]);

  return (
    <>
      <QFlex pb="8px">
        <QButton
          leftIcon="ArrowLeft"
          variant="link"
          onClick={() => navigate('/doc-templates')}
          data-cy="back-to-templates-button"
        >
          <QText lineHeight={8}>{t('template.details.header.backButton')}</QText>
        </QButton>
        <QSpacer />
        <QOpenPropertiesPanelButton />
      </QFlex>
      <QHeader showDivider={false}>
        <QTitle>
          {data.name}
          {data.parent && (
            <QBox ml="2" data-cy="template-id" display="inline-block">
              <InheritedTemplateIcon templateId={data.id} parentName={data.parent.name} />
            </QBox>
          )}
          <QBadge ml="2" data-cy="template-prefix">
            {data.prefix}
          </QBadge>
        </QTitle>
        <QHorizontalMetadata>
          <QTag data-cy="template-status" variantColor={getTagColor(data.status)}>
            {getTagText(data.status)}
          </QTag>
          <QVersionMetadata data-cy="template-version" value={String(data.version)} />
          <QOwnerMetadata value={getOwnerText(data)} />
          {data.modified_time && <QLastModifiedMetadata value={data.modified_time} />}
        </QHorizontalMetadata>
        <QButtonGroup>
          {canExport && <ExportDocumentTemplateButton template={data} />}
          <TemplateVersionSelect currentVersionId={data.id} versions={versions} />
          <TemplateHeaderButtons
            template={data}
            onSaveSections={onSaveSections}
            onBeforeMakeEffective={onBeforeMakeEffective}
          />
        </QButtonGroup>
        {showDraftInProgressAlert && (
          <QAlertBox status="info" alignItems="center">
            <QFlex alignItems="baseline">
              <QAlertTitle>{t('template.details.header.alert.newDraft')}</QAlertTitle>
              <QAlertDescription>{t('template.details.header.alert.onlyOneDraft')}</QAlertDescription>
              <QSpacer />
              <QButton data-cy="show-draft-button" variant="outline" onClick={showDraft}>
                {t('template.details.header.alert.viewDraft')}
              </QButton>
            </QFlex>
          </QAlertBox>
        )}
      </QHeader>
      {canUpgradeDocuments && (
        <QBox pt="16px">
          <OldDraftsAlert templateId={String(data.id)} version={data.major_version + '.' + data.minor_version} />
        </QBox>
      )}
      {isInherited && <QDivider mt={4} />}
    </>
  );
};
